import React, { useState, useEffect } from 'react';
import translate from "../../../config/translations/translate";
import {
  Table,
  UxTooltip
} from 'botbit-ui-components';
import DashboardPeriodSelector from '../../dashboardV2/dashboardPeriodSelector/DashboardPeriodSelector';
import { getQRScansLeaderboardByCompany, getQRScansLeaderboardByStore } from '../../../api/reviewLeaderboardApi';
import { QRScanLeaderboardIcon } from '../../dashboardV2/DashboardV2ContainerIcons';
import './ReviewLeaderboardCompanyView.scss';
import { useHistory } from 'react-router-dom';
import { ReconnectGBPIcon } from '../../dashboardV2/localSEOTable/localSEOTableIcons.js';
import withEventTracking, { trackers } from '../../../config/withEventTracking';
import { setSelectedStoreForReviewLeaderboard } from '../../../commons/components/application/applicationActions';
import { useDispatch } from 'react-redux';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ReviewLeaderBoardCompanyView = ({ 
  companyId,
  companyName,
  t,
  trackEvent
}) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentOrder, setCurrentOrder] = useState({ name: 'position', direction: 'asc' });
  const [localSelectedPeriod, setLocalSelectedPeriod] = useState({
    value: 'LAST_7_DAYS'
  });
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const modalTimePeriodOptions = [
    { value: 'LAST_7_DAYS', label: t('timePeriod.options.last7Days') },
    { value: 'LAST_15_DAYS', label: t('timePeriod.options.last15Days') },
    { value: 'THIS_MONTH', label: t('timePeriod.options.thisMonth') },
    { value: 'PAST_MONTH', label: t('timePeriod.options.pastMonth') },
    { value: 'LAST_3_MONTHS', label: t('timePeriod.options.last3Months') },
    { value: 'LAST_6_MONTHS', label: t('timePeriod.options.last6Months') },
    { value: 'LAST_12_MONTHS', label: t('timePeriod.options.last12Months') },
  ];


  useEffect(() => {
    if (companyId) {
      fetchLeaderboardData();
      
      // Track Review Leaderboard company view event
      trackEvent('Reviews_Leaderboard_company_view', {
        companyId: companyId
      }, false, [trackers.MIXPANEL, trackers.INTERCOM]);
    }
  }, [companyId, localSelectedPeriod]);
  
  // Fetch all employees when leaderboard data changes
  useEffect(() => {
    if (leaderboardData.length > 0) {
      fetchAllEmployeesByCompany();
    }
  }, [leaderboardData]);

  const handleLocalPeriodChange = (period) => {
    setLocalSelectedPeriod(period);
  };

  const fetchLeaderboardData = async () => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getQRScansLeaderboardByCompany(companyId, { timePeriod: localSelectedPeriod });
      setLeaderboardData(response.data.data.items || []);
    } catch (error) {
      console.error("Error fetching QR Scans Company Leaderboard data:", error);
      setError(t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page, order, filters) => {
    const orderChanged = order && (currentOrder.name !== order.name || currentOrder.direction !== order.direction);
    
    if (orderChanged && order) {
      setCurrentOrder(order);
    }
        
    const sortName = order ? order.name : currentOrder.name;
    const sortDirection = order ? order.direction : currentOrder.direction;
    
    const sortedData = [...leaderboardData].sort((a, b) => {
      const valueA = a[sortName];
      const valueB = b[sortName];
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      }
      
      return sortDirection === 'asc' 
        ? valueA - valueB 
        : valueB - valueA;
    });

    return {
      data: {
        data: {
          items: sortedData,
          totalSize: sortedData.length,
          pageSize: sortedData.length
        }
      }
    };
  };

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type === 'sort') {
      setCurrentOrder({
        name: sortField,
        direction: sortOrder
      });
    }
  };

  const columns = [
    {
      dataField: 'position',
      title: '#',
      size: 10,
      sortable: true,
      formatter: (cellValue) => (
        <span className="leaderboard-position">#{cellValue}</span>
      ),
    },
    {
      dataField: 'storeName',
      title: (
        <div className="column-with-tooltip">
          {t('columns.store')}
          <UxTooltip
            message={t('columns.store.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="store-name">{cellValue || t('defaultCompanyName')}</span>
      ),
      sortable: true,
      size: 30,
    },
    {
      dataField: 'scanCount',
      title: (
        <div className="column-with-tooltip">
          {t('columns.qrScans')}
          <UxTooltip
            message={t('columns.qrScans.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="qr-scans-value">{cellValue}</span>
      ),
      sortable: true,
      size: 20,
      tooltip: {
        title: t('columns.qrScans.tooltip.title'),
        content: t('columns.qrScans.tooltip.content')
      }
    },
    {
      dataField: 'localPercentage',
      title: (
        <div className="column-with-tooltip">
          {t('columns.localPercentage')}
          <UxTooltip
            message={t('columns.localPercentage.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="percentage-value">{Number(cellValue).toFixed(0)}%</span>
      ),
      sortable: true,
      size: 15,
      tooltip: {
        title: t('columns.localPercentage.tooltip.title'),
        content: t('columns.localPercentage.tooltip.content')
      }
    },
    {
      dataField: 'touristPercentage',
      title: (
        <div className="column-with-tooltip">
          {t('columns.touristPercentage')}
          <UxTooltip
            message={t('columns.touristPercentage.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue) => (
        <span className="percentage-value">{Number(cellValue).toFixed(0)}%</span>
      ),
      sortable: true,
      size: 12,
      tooltip: {
        title: t('columns.touristPercentage.tooltip.title'),
        content: t('columns.touristPercentage.tooltip.content')
      }
    },
    {
      dataField: 'actions',
      title: (
        <div className="column-with-tooltip">
          {t('columns.actions')}
          <UxTooltip
            message={t('columns.actions.tooltip.content')}
            position="top"
          />
        </div>
      ),
      formatter: (cellValue, row) => {
        return (
          <button className="details-button" onClick={() => {
            history.push(`/review-leaderboard/${row.storeId}`);
            dispatch(setSelectedStoreForReviewLeaderboard(row));
          }}>
            {t('columns.actions.details')}
          </button>
        )
      },
      sortable: false,
      size: 13,
      tooltip: {
        title: t('columns.actions.tooltip.title'),
        content: t('columns.actions.tooltip.content')
      }
    }
  ];

  // función que define el contenido para filas sobrescritas
  const rowOverrideContent = (element) => {
    return (
        <div className="custom-row">
            <span className="position-span"> #{element.position} </span>
            <span className="store-name-span"> {element.storeName} </span>
            <div className="custom-row-message-container">
                <ReconnectGBPIcon/>
                <span className="custom-row-message-span"> 
                    { t('columns.createEmployees.noEmployeesMessage') } 
                </span>
                
                <span className="custom-row-message-span-link" onClick={() => {
                  history.push('/employee/new');
                  dispatch(setSelectedStoreForReviewLeaderboard(element));
                }} >
                    {t('columns.createEmployees.createEmployeesMessage')}
                </span>
            </div>
        </div>
    );
  };

  const fetchAllEmployeesByCompany = async () => {
    console.log('fetchAllEmployeesByCompany');
    if (!companyId) return;
    
    try {
      // Get all stores for the company from the leaderboard data
      const storeIds = leaderboardData.map(store => store.storeId);
      
      // Fetch employees from all stores
      const storePromises = storeIds.map(async (storeId) => {
        try {
          const response = await getQRScansLeaderboardByStore(storeId, { timePeriod: localSelectedPeriod });
          console.log('response', response);
          const storeEmployees = response.data.items || [];
          console.log('storeEmployees', storeEmployees);
          // Add store information to each employee
          const storeInfo = leaderboardData.find(store => store.storeId === storeId);
          const employeesWithStoreInfo = storeEmployees.map(employee => ({
            ...employee,
            storeName: (storeInfo && storeInfo.storeName) || 'store',
            storeId: storeId
          }));
          
          return employeesWithStoreInfo;
        } catch (error) {
          console.error(`Error fetching employees for store ${storeId}:`, error);
          return [];
        }
      });
      console.log('storePromises', storePromises);

      // Wait for all stores to complete 
      const results = await Promise.all(storePromises);
      console.log('results', results);
      // Flatten results into single array
      const allEmployeesData = results.flat();
      console.log('allEmployeesData', allEmployeesData);
      setAllEmployees(allEmployeesData);
    } catch (error) {
      console.error('Error fetching all employees:', error);
    }
  };

  const handleDownloadAllQRs = async () => {
    if (!allEmployees || allEmployees.length === 0) {
      console.warn('No employees to download QRs');
      return;
    }

    setDownloadingAll(true);

    try {
      // If allEmployees is not loaded yet, fetch them first
      let employeesToDownload = allEmployees;
      if (!employeesToDownload || employeesToDownload.length === 0) {
        await fetchAllEmployeesByCompany();
        employeesToDownload = allEmployees;
      }

      if (!employeesToDownload || employeesToDownload.length === 0) {
        MySwal.fire({
          type: "warning",
          text: t("noEmployeesToDownload"),
          confirmButtonText: t("close"),
          confirmButtonColor: "#ff7043"
        });
        return;
      }

      const zip = new JSZip();

      // Helper function to normalize accented characters
      const normalizeText = (text) => {
        return text
          .normalize('NFD') // Decompose characters with their accents
          .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
          .replace(/\s+/g, '-') // Convert spaces to hyphens
          .replace(/[^A-Za-z0-9-]/g, ''); // Remove other special characters
      };

      // Process all employees in parallel
      const downloadPromises = employeesToDownload.map(async (employee, index) => {
        if (!employee.qrLink) {
          console.warn(`No QR link for employee: ${employee.adminName || 'Unknown'}`);
          return null;
        }

        try {
          const fullUrl = employee.qrLink.startsWith('http') ? employee.qrLink : `https://${employee.qrLink}`;
          const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(fullUrl)}`;

          // Generate filename: store + employee name + employee last name
          const cleanStoreName = normalizeText(employee.storeName || 'store');
          const cleanName = normalizeText(employee.adminName || 'employee');
          const cleanLastName = normalizeText(employee.adminLastName || '');
          const fileName = cleanLastName 
            ? `${cleanStoreName}_${cleanName}_${cleanLastName}.png`
            : `${cleanStoreName}_${cleanName}.png`;

          // Fetch the QR code
          const response = await fetch(qrCodeUrl);
          if (response.ok) {
            const blob = await response.blob();
            zip.file(fileName, blob);
            return { success: true, fileName };
          } else {
            console.error(`Error fetching QR for ${employee.adminName || 'employee'}: ${response.status}`);
            return { success: false, error: `HTTP ${response.status}` };
          }
        } catch (error) {
          console.error(`Error processing QR for ${employee.adminName || 'employee'}:`, error);
          return { success: false, error: error.message };
        }
      });

      // Wait for all downloads to complete
      const results = await Promise.all(downloadPromises);
      const successfulDownloads = results.filter(result => result && result.success);
      
      if (successfulDownloads.length === 0) {
        throw new Error('No QRs were successfully downloaded');
      }

      // Generate and download the ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrs_employees_${companyName ? companyName : 'company'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      MySwal.fire({
        type: "success",
        text: t("downloadSuccess"),
        confirmButtonText: t("close"),
        confirmButtonColor: "#ff7043"
      });

    } catch (error) {
      console.error('Error downloading all QRs:', error);
      MySwal.fire({
        type: "error",
        text: t("downloadError"),
        confirmButtonText: t("close"),
        confirmButtonColor: "#ff7043"
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  

  return (
    <div>
      <div className="qr-scans-leaderboard-header">
        <h1 className="company-name">{companyName || t('defaultCompanyName')}</h1>
      </div>
      
      <div className="qr-scans-leaderboard-content">
        <div className="qr-scans-section-header">
          <div className="qr-icon-title">
            <div className="qr-icon">
              <QRScanLeaderboardIcon />   
            </div>
            <div className="title-description">
              <h2>{t('title')}</h2>
              <p>{t('description')} {t('rankingLink')}</p>
            </div>
          </div>
          <div className="configure-button-container">
            <button
              className="configure-button"
              onClick={() => history.push('/review-leaderboard/config')}
            >
              {t('buttons.configure')}
            </button>
          </div>
        </div>
        
        <div className="qr-scans-table-section">

          <div className="qr-scans-table-header">
            <h3>{t('tableTitle')}</h3>
            <div className="info-tooltip">
              <UxTooltip 
                title={t('tableTooltip.title')} 
                content={t('tableTooltip.content')} 
              />
            </div>
            
            {/* Download All QRs Button */}
            {!loading && !error && allEmployees.length > 0 && (
              <button
                className="download-all-qrs-button"
                onClick={handleDownloadAllQRs}
                disabled={downloadingAll}
              >
                {downloadingAll ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {t('buttons.downloadingAll')}
                  </>
                ) : (
                  <>
                    {t('buttons.downloadAll')}
                  </>
                )}
              </button>
            )}

            <div className="period-selector-container">
              <DashboardPeriodSelector
                timePeriodOptions={modalTimePeriodOptions}
                selectedPeriod={localSelectedPeriod}
                handlePeriodChange={handleLocalPeriodChange}
                t={t}
                useDisabledDays={false}
              />
            </div>
          </div>
          
          <div className="qr-scans-table-container">
            {loading && (
              <div className="loading-message">{t('loading')}</div> 
            )}
            
            {error && (
              <div className="error-message">{error}</div>
            )}
            
            {!loading && !error && leaderboardData.length === 0 && (
              <div className="no-data-message">{t('noData')}</div>
            )}
            
            {!loading && !error && leaderboardData.length > 0 && (
              <Table
                getData={getTableData}
                columns={columns}
                showMobileColumnTitles={true}
                loading={loading}
                dataPath="data.data.items"
                elementKey="storeId"
                pagination={false}
                filterValues={{}}
                onTableChange={handleTableChange}
                remote={{ sort: true }}
                rowOverrideCondition={(element) => element.numberOfEmployees === 0}
                rowOverrideContent={rowOverrideContent}
              />
            )}
          </div>
        </div>
        
        
      </div>
    </div>
  );
};

ReviewLeaderBoardCompanyView.translations = {
  title: {
    es: "Tabla QR de posiciones",
    en: "QR Scan Leaderboard"
  },
  description: {
    es: "Mejora el potencial de tus sucursales para pedir nuevos QR Scans.",
    en: "Improve your stores potential to request new QR Scans."
  },
  rankingLink: {
    es: "Aquí puedes ver el ranking en base a los pedidos.",
    en: "Here you can see the ranking based on orders."
  },
  tableTitle: {
    es: "QR Scans por sucursal",
    en: "QR Scans per store"
  },
  locationSelector: {
    label: {
      es: "Tienda",
      en: "Store"
    }
  },
  timePeriod: {
    label: {
      es: "Período de tiempo",
      en: "Time period"
    },
    options: {
      last7Days: {
        es: "Últimos 7 días",
        en: "Last 7 days"
      },
      last15Days: {
        es: "Últimos 15 días",
        en: "Last 15 days"
      },
      last30Days: {
        es: "Últimos 30 días",
        en: "Last 30 days"
      },
      thisMonth: {
        es: "Este mes",
        en: "This month"
      },
      pastMonth: {
        es: "Mes pasado",
        en: "Last month"
      },
      last3Months: {
        es: "Últimos 3 meses",
        en: "Last 3 months"
      },
      last6Months: {
        es: "Últimos 6 meses",
        en: "Last 6 months"
      },
      last12Months: {
        es: "Últimos 12 meses",
        en: "Last 12 months"
      }
    }
  },
  defaultCompanyName: {
    es: "Compañía",
    en: "Company"
  },
  loading: {
    es: "Cargando datos...",
    en: "Loading data..."
  },
  noData: {
    es: "No hay datos disponibles",
    en: "No data available"
  },
  tableTooltip: {
    title: {
      es: "QR Scans",
      en: "QR Scans"
    },
    content: {
      es: "tooltip.",
      en: "tooltip."
    }
  },
  columns: {
    store: {
      es: "Sucursal",
      en: "Store",
      tooltip: {
        content: {
          es: "Nombre de la sucursal",
          en: "Store name"
        }
      }
    },
    qrScans: {
      es: "QR Scans",
      en: "QR Scans",
      tooltip: {
        title: {
          es: "QR Scans",
          en: "QR Scans"
        },
        content: {
          es: "Es la cantidad de scans únicos de los QRs de todos los mozos de esta tienda sumados. Esta métrica diferencia por cuenta de Google, asegurando confiabilidad en la unicidad",
          en: "It is the number of unique scans of the QRs of all the waiters in this store added together. This metric differentiates by Google account, ensuring reliability in uniqueness"
        }
      }
    },
    localPercentage: {
      es: "% locales",
      en: "% locals",
      tooltip: {
        title: {
          es: "Porcentaje local",
          en: "Local percentage"
        },
        content: {
          es: "El % de scans que se corresponden con un usuario del mismo pais que la tienda. Utiliza el idioma del dispositivo",
          en: "The % of scans that correspond to a user from the same country as the store. Uses the device language"
        }
      }
    },
    touristPercentage: {
      es: "% turistas",
      en: "% tourists",
      tooltip: {
        title: {
          es: "Porcentaje de turistas",
          en: "Tourist percentage"
        },
        content: {
          es: "El % de scans que se corresponden con un usuario de un pais distinto que la tienda. Utiliza el idioma del dispositivo",
          en: "The % of scans that correspond to a user from a different country than the store. Uses the device language"
        }
      }
    },
    actions: {
      es: "Acciones",
      en: "Actions",
      tooltip: {
        title: {
          es: "Acciones",
          en: "Actions"
        },
        content: {
          es: "Clickea aquí para ver los detalles de la tienda",
          en: "Click here to see the store details"
        }
      },
      details: {
        es: "Ver detalles",
        en: "View details"
      }
    },
    createEmployees: {
      noEmployeesMessage: {
        es: "No hay empleados creados.",
        en: "No employees created.",
      },
      createEmployeesMessage: {
        es: "Crea empleados para poder usar los scans",
        en: "Create employees to use the scans"
      },
    }
  },
  buttons: {
    downloadAll: {
      es: "Descargar todos los QRs",
      en: "Download All QRs"
    },
    downloadingAll: {
      es: "Descargando...",
      en: "Downloading..."
    },
    abmEmployees: {
      es: "Empleados",
      en: "Employees"
    },
    configure: {
      es: "Configurar",
      en: "Configure"
    }
  },
  errors: {
    fetchFailed: {
      es: "Error al cargar los datos. Por favor, intente nuevamente.",
      en: "Failed to load data. Please try again."
    }
  },
  downloadSuccess: {
    es: "QR Codes descargados exitosamente.",
    en: "QR Codes downloaded successfully."
  },
  downloadError: {
    es: "Error al descargar los QR Codes. Por favor, intente nuevamente.",
    en: "Error downloading QR Codes. Please try again."
  },
  noEmployeesToDownload: {
    es: "No hay empleados para descargar QR Codes.",
    en: "No employees to download QR Codes."
  },
  close: {
    es: "Cerrar",
    en: "Close"
  }
};

export default withEventTracking(translate(ReviewLeaderBoardCompanyView)); 